// ivyChatService.js
// Servicio para consultar modelos de IA para el chatbot Ivy especializado en desarrollo infantil

// Importar OpenAI para usar el nuevo modelo DeepSeek
import { OpenAI } from "openai";

/**
 * Calcula la distancia de Levenshtein entre dos cadenas
 * @param {string} a - Primera cadena
 * @param {string} b - Segunda cadena
 * @returns {number} - Distancia de edición
 */
const calcularDistanciaLevenshtein = (a, b) => {
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;
  
  const matriz = Array(b.length + 1).fill(null).map(() => Array(a.length + 1).fill(null));
  
  for (let i = 0; i <= a.length; i++) matriz[0][i] = i;
  for (let j = 0; j <= b.length; j++) matriz[j][0] = j;
  
  for (let j = 1; j <= b.length; j++) {
    for (let i = 1; i <= a.length; i++) {
      const costo = a[i - 1] === b[j - 1] ? 0 : 1;
      matriz[j][i] = Math.min(
        matriz[j][i - 1] + 1,     // inserción
        matriz[j - 1][i] + 1,     // eliminación
        matriz[j - 1][i - 1] + costo // sustitución
      );
    }
  }
  
  return matriz[b.length][a.length];
};

/**
 * Verifica si dos palabras son similares usando distancia de edición
 * @param {string} palabra1 - Primera palabra
 * @param {string} palabra2 - Segunda palabra
 * @param {number} umbral - Umbral de similitud (por defecto 0.7)
 * @returns {boolean} - Verdadero si son similares
 */
const sonPalabrasSimilares = (palabra1, palabra2, umbral = 0.7) => {
  if (palabra1.length < 3 || palabra2.length < 3) {
    return palabra1 === palabra2;
  }
  
  const distancia = calcularDistanciaLevenshtein(palabra1, palabra2);
  const longitudMaxima = Math.max(palabra1.length, palabra2.length);
  const similitud = 1 - (distancia / longitudMaxima);
  
  return similitud >= umbral;
};

/**
 * Función para normalizar texto y corregir errores ortográficos comunes
 * @param {string} text - Texto a normalizar
 * @returns {string} - Texto normalizado
 */
const normalizarTexto = (text) => {
  if (!text) return '';
  
  // Convertir a minúsculas y eliminar acentos
  let normalizado = text.toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s]/g, ' ') // Eliminar caracteres especiales
    .replace(/\s+/g, ' ') // Normalizar espacios
    .trim();
  
  // Corregir errores comunes de tipeo
  const correcciones = {
    'ñ': 'n',
    'nino': 'nino',
    'nina': 'nina',
    'bebe': 'bebe',
    'hla': 'hola',
    'ola': 'hola',
    'desarollo': 'desarrollo',
    'desarrolo': 'desarrollo',
    'estimulacion': 'estimulacion',
    'estimulasion': 'estimulacion',
    'criansa': 'crianza',
    'lenguage': 'lenguaje',
    'abla': 'habla',
    'avla': 'habla',
    'alimentasion': 'alimentacion',
    'alimentacion': 'alimentacion',
    'leche materna': 'leche materna',
    'dormír': 'dormir',
    'sueno': 'sueno',
    'comprotamiento': 'comportamiento',
    'berrinche': 'berrinche',
    'berrinches': 'berrinche',
    'vacuna': 'vacuna',
    'bacuna': 'vacuna',
    'pediatra': 'pediatra',
    'creciminto': 'crecimiento',
    'fievre': 'fiebre',
    'cabesita': 'cabeza',
    'cabesa': 'cabeza',
    'gargante': 'garganta',
    'toz': 'tos',
    'diarera': 'diarrea',
    'como te yamas': 'como te llamas',
    'como te llams': 'como te llamas',
    'que pueds hacer': 'que puedes hacer',
    'k puedes hacer': 'que puedes hacer',
    'q puedes hacer': 'que puedes hacer',
    'saludos': 'hola',
    'economia': 'economia', 
    'gestion': 'gestion',
    'administracion': 'administracion',
    'administrasion': 'administracion',
    'finansa': 'finanza',
    'finanza': 'finanza',
    'negosio': 'negocio',
    'negocio': 'negocio',
    'aprendisaje': 'aprendizaje',
    'metodo': 'metodo',
    'metodologia': 'metodologia',
    'cognitivo': 'cognitivo',
    'pedagogia': 'pedagogia',
    'pedagojia': 'pedagogia',
    'tecnica': 'tecnica',
    'profesional': 'profesional',
    // Errores comunes adicionales
    'puedes': 'puedes',
    'pueds': 'puedes',
    'pudes': 'puedes',
    'puedez': 'puedes',
    'hacer': 'hacer',
    'aser': 'hacer',
    'acer': 'hacer',
    'haser': 'hacer',
    'ayudar': 'ayudar',
    'ayuda': 'ayuda',
    'alluda': 'ayuda',
    'aiuda': 'ayuda',
    'profesor': 'profesor',
    'profeso': 'profesor',
    'profesora': 'profesora',
    'profe': 'profesor',
    'maestro': 'maestro',
    'maestra': 'maestra',
    'centro': 'centro',
    'sentro': 'centro',
    'soy': 'soy',
    'trabajo': 'trabajo',
    'travajo': 'trabajo',
    'trabjo': 'trabajo',
    'como': 'como',
    'komo': 'como',
    'cmo': 'como',
    'que': 'que',
    'qe': 'que',
    'k': 'que',
    'q': 'que',
    'faltas': 'faltas',
    'falktas': 'faltas',
    'falta': 'falta',
    'ortografia': 'ortografia',
    'hortografia': 'ortografia',
    'ortografica': 'ortografia',
    'escribir': 'escribir',
    'escrivir': 'escribir',
    'eskribir': 'escribir',
    'responder': 'responder',
    'responde': 'responder',
    'respnder': 'responder',
    'inteligencia': 'inteligencia',
    'intelijencia': 'inteligencia',
    'artificial': 'artificial',
    'artifisial': 'artificial'
  };
  
  // Reemplazar palabras con errores por sus versiones corregidas
  Object.keys(correcciones).forEach(error => {
    normalizado = normalizado.replace(new RegExp(error, 'g'), correcciones[error]);
  });
  
  return normalizado;
};

/**
 * Verifica coincidencias inteligentes usando similitud de texto
 * @param {string} textoNormalizado - Texto del usuario normalizado
 * @param {Array} listaTerminos - Lista de términos a verificar
 * @param {number} umbralSimilitud - Umbral de similitud (por defecto 0.75)
 * @returns {boolean} - Verdadero si encuentra coincidencias
 */
const verificarCoincidenciaInteligente = (textoNormalizado, listaTerminos, umbralSimilitud = 0.75) => {
  const palabrasUsuario = textoNormalizado.split(' ').filter(p => p.length > 2);
  
  // Primero verificar coincidencias exactas (más rápido)
  if (listaTerminos.some(termino => textoNormalizado.includes(termino))) {
    return true;
  }
  
  // Luego verificar similitudes para palabras más largas
  for (const palabraUsuario of palabrasUsuario) {
    if (palabraUsuario.length >= 4) { // Solo para palabras de 4+ caracteres
      for (const termino of listaTerminos) {
        const palabrasTermino = termino.split(' ');
        for (const palabraTermino of palabrasTermino) {
          if (palabraTermino.length >= 4 && sonPalabrasSimilares(palabraUsuario, palabraTermino, umbralSimilitud)) {
            console.log(`Similitud detectada: "${palabraUsuario}" ≈ "${palabraTermino}"`);
            return true;
          }
        }
      }
    }
  }
  
  return false;
};

/**
 * Verifica coincidencias usando múltiples estrategias
 * @param {string} textoNormalizado - Texto del usuario normalizado
 * @param {Array} listaTerminos - Lista de términos a verificar
 * @returns {boolean} - Verdadero si encuentra coincidencias
 */
const verificarCoincidenciaFlexible = (textoNormalizado, listaTerminos) => {
  // Estrategia 1: Coincidencia exacta
  if (verificarCoincidenciaInteligente(textoNormalizado, listaTerminos, 1.0)) {
    return true;
  }
  
  // Estrategia 2: Similitud alta (errores menores)
  if (verificarCoincidenciaInteligente(textoNormalizado, listaTerminos, 0.85)) {
    return true;
  }
  
  // Estrategia 3: Similitud moderada SOLO para palabras clave muy importantes
  // RESTRICTIVO: solo aplicar a palabras muy específicas y de importancia crítica
  const terminosImportantes = listaTerminos.filter(t => 
    (t.includes('politica') || t.includes('religion') || t.includes('magia') || 
     t.includes('software') || t.includes('programacion') || t.includes('codigo'))
  );
  
  if (terminosImportantes.length > 0 && verificarCoincidenciaInteligente(textoNormalizado, terminosImportantes, 0.75)) {
    return true;
  }
  
  return false;
};

/**
 * Determina si un tema está relacionado con lo que puede responder Ivy según el rol del usuario
 * @param {string} text - Texto para evaluar
 * @param {string} userRole - Rol del usuario: "Padre", "Profesor" o "Administrador"
 * @returns {boolean} - Verdadero si el tema está relacionado con la especialidad del rol
 */
/**
 * [DEPRECATED - Ya no se utiliza en la nueva lógica permisiva]
 * Función para obtener preguntas básicas comunes
 * @returns {Object} - Objeto con arrays de preguntas básicas
 */
const obtenerPreguntasBasicas = () => {
  return {
    preguntasNombre: [
      "como te llamas", "cual es tu nombre", "cual es tu nombre", "quien eres", "quien eres", "tu nombre es",
      "me dices tu nombre", "puedes decirme tu nombre", "te llamas", "como te dices", "como te dicen",
      "como te llama", "komo te llamas", "qien eres", "kien eres", "como t llamas", "como te llmas"
    ],
    preguntasCapacidades: [
      "que puedes hacer", "que puedes hacer", "que sabes hacer", "que sabes hacer", "en que me puedes ayudar",
      "como me puedes ayudar", "cuales son tus funciones", "para que sirves", "que me puedes decir",
      "que tipo de preguntas", "como funciona", "como funcionas", "q puedes hacer", "k puedes hacer",
      "q sabes hacer", "k sabes", "ke sabes", "q me puedes", "k me puedes", "para k sirves", "para q sirves",
      "que haces", "que ofreces", "que servicios", "que ayuda", "que apoyo", "que asistencia",
      "en que me ayudas", "como me asistes", "que me recomiendas", "que me sugieres", "que me aconsejas",
      "cuales son tus habilidades", "que capacidades tienes", "que competencias", "que especialidades",
      "en que eres experto", "en que te especializas", "que dominas", "que conoces", "de que sabes"
    ],
    saludos: [
      "hola", "buenos dias", "buenas tardes", "buenas noches", "hi", "hello", "hey", "ola", "hla", 
      "saludos", "buen dia", "buenas", "que tal", "como estas", "que hay", "que onda", "holi"
    ],
    despedidas: [
      "adios", "hasta luego", "hasta pronto", "chau", "bye", "goodbye", "see you later", "hasta la vista", 
      "hasta la proxima", "nos vemos", "cuidate", "que tengas buen dia", "que estes bien"
    ]
  };
};

/**
 * Función para obtener preguntas específicas por rol
 * @param {string} rol - Rol del usuario
 * @returns {Array} - Array de preguntas específicas del rol
 */
const obtenerPreguntasPorRol = (rol) => {
  switch (rol.toLowerCase()) {
    case "profesor":
      return [
        "como profesor", "soy profesor", "soy profesora", "trabajo como profesor", "trabajo como profesora",
        "profesor del centro", "profesora del centro", "docente", "maestro", "maestra", "educador", "educadora",
        "enseño", "doy clases", "trabajo en educacion", "trabajo en la escuela", "trabajo en el colegio",
        "mi trabajo es enseñar", "mi profesion es profesor", "mi profesion es profesora", "trabajo con niños",
        "trabajo con estudiantes", "trabajo con alumnos", "en mi aula", "en mi clase", "mis estudiantes",
        "mis alumnos", "planificar clases", "evaluar estudiantes", "metodologia de enseñanza",
        "docente del centro", "maestro del centro", "maestra del centro", "educador del centro",
        "educadora del centro", "profesor en el centro", "profesora en el centro", "trabajo en el centro",
        "laboro como profesor", "laboro como profesora", "me desempeño como profesor", "me desempeño como profesora"
      ];
    case "administrador":
      return [
        "como administrador", "soy administrador", "soy administradora", "trabajo como administrador",
        "trabajo como administradora", "director", "directora", "gerente", "coordinador", "coordinadora",
        "jefe", "jefa", "supervisor", "supervisora", "administro", "dirijo", "coordino", "superviso",
        "gestiono", "manejo el centro", "dirijo el centro", "administro el centro", "trabajo en administracion",
        "trabajo en gestion", "mi trabajo es administrar", "mi trabajo es dirigir", "mi trabajo es gestionar",
        "presupuesto", "finanzas del centro", "recursos del centro", "personal del centro", "equipo de trabajo",
        "planificacion estrategica", "toma de decisiones", "liderazgo", "gestion de recursos"
      ];
    default:
      return [];
  }
};

/**
 * Función para obtener temas no permitidos
 * @returns {Array} - Array de temas no permitidos
 */
const obtenerTemasNoPermitidos = () => {
  return [
    // ========== PROGRAMACIÓN Y DESARROLLO TÉCNICO DE SOFTWARE ==========
    // Lenguajes de programación
    "python", "javascript", "java", "csharp", "c++", "c#", "rust", "go", "kotlin", "swift", "typescript",
    "php", "ruby", "perl", "scala", "haskell", "lisp", "clojure", "r language", "matlab", "vhdl", "verilog",
    // Frameworks y librerías
    "react", "angular", "vue", "django", "flask", "spring", "aspnet", "laravel", "symfony", "express",
    "node.js", "nodejs", "nextjs", "nuxt", "fastapi", "fastify", "nestjs", "tensorflow", "pytorch",
    "scikit-learn", "keras", "pandas", "numpy", "matplotlib", "jupyter", "spark", "hadoop", "kafka",
    // Herramientas y tecnologías
    "git", "github", "gitlab", "docker", "kubernetes", "jenkins", "circleci", "travis ci", "github actions",
    "webpack", "babel", "webpack", "rollup", "gulp", "grunt", "npm", "yarn", "pip", "maven", "gradle",
    "sql", "mysql", "postgresql", "mongodb", "cassandra", "elasticsearch", "redis", "dynamodb",
    "aws", "azure", "gcp", "heroku", "firebase", "netlify", "vercel", "render", "railway",
    // Conceptos técnicos de software
    "programacion", "desarrollo software", "desarrollo web", "desarrollo movil", "backend", "frontend",
    "full stack", "api rest", "graphql", "microservicios", "arquitectura software", "diseño patrones",
    "algoritmos", "estructura datos", "compilador", "interprete", "debugging", "testing unitario",
    "integracion continua", "deployment", "devops", "cloud computing", "serverless", "contenedores",
    "virtualizacion", "machine learning", "deep learning", "inteligencia artificial", "ia", "nlp",
    "visión artificial", "big data", "data science", "data engineering", "analytics", "business intelligence",
    "cifrado", "encriptacion", "hash", "blockchain", "web3", "smart contracts", "dapps",
    "protocolo http", "protocolo https", "tcp/ip", "dns", "firewall", "vpn", "proxy", "load balancer",
    // Conceptos relacionados
    "codigo fuente", "lineas codigo", "bug", "debug", "error", "exception", "stack overflow", "memoria heap",
    "compilacion", "transpilacion", "minificacion", "uglification", "bundling", "caching",
    // Más tecnología general
    "software", "hardware", "procesador", "memoria ram", "disco duro", "gpu", "cpu", "chipset",
    "motherboard", "bios", "firmware", "sistema operativo", "linux", "windows", "macos",
    "redes", "networking", "telecomunicaciones", "5g", "4g", "3g", "wifi", "bluetooth", "iot",
    "electronica", "semiconductores", "circuito integrado", "transistor", "diodo", "resistencia",
    
    // ========== TEMAS GENERALES (SIN RELACIÓN CON NEGOCIO INFANTIL) ==========
    // Política, religión, ideología
    "politica", "gobierno", "presidente", "congreso", "senado", "partido politico", "elecciones",
    "ideologia", "comunismo", "capitalismo", "socialismo", "anarquismo", "fascismo",
    "religion", "iglesia", "dios", "jesus", "virgen", "santo", "angel", "demonio", "satanas",
    "teologia", "fe", "creencia religiosa", "culto", "secta", "satanismo", "ocultismo", "magia",


    // Historia, geografía, ciencias generales
    "historia", "batalla", "guerra", "imperio", "civilizacion", "arqueologia", "antropologia",
    "geografia", "montaña", "oceano", "pais", "ciudad", "continente", "mapa", "topografia",
    "matematicas avanzadas", "algebra", "geometria avanzada", "calculo", "ecuaciones diferenciales",
    "fisica", "mecanica", "termodinamica", "electromagnetismo", "optica", "acustica", "relatividad",
    "quimica", "tabla periodica", "reaccion quimica", "osmosis", "acido base", "oxido reduccion",
    "biologia avanzada", "genetica", "evolucion", "zoologia", "botanica", "microbiologia", "ecologia",
    
    // Derecho, leyes, asuntos legales
    "derecho", "leyes", "abogacia", "abogado", "juicio", "corte", "juzgado", "fiscalia",
    "contratos", "clausula", "responsabilidad civil", "responsabilidad penal", "negligencia",
    "inmobiliaria", "construccion", "arquitectura", "ingenieria civil", "urbanismo", "catastro",
    
    // Educación NO relacionada con infancia
    "universidad", "educacion superior", "postgrado", "maestria", "doctorado", "phd",
    "investigacion cientifica", "laboratorio", "experimento", "articulo cientifico", "publicacion",
    "educacion secundaria", "bachillerato", "educacion adultos", "formacion profesional",
    
    // Empresa y corporaciones
    "empresa multinacional", "corporacion", "startup", "unicornio", "venture capital", "angel investor", 
    "e-commerce", "marketplace", "affiliate marketing", "seo", "sem",
    "recursos humanos empresariales", "reclutamiento", "seleccion personal", "nomina", "liquidacion",
    "finanzas corporativas", "contabilidad empresarial", "auditoria", "balance general", "estado resultado",
    "consultoria", "fusiones", "adquisiciones", "ipo", "salida mercado",
    "liderazgo empresarial", "management", "organizacion", "jerarquia", "delegacion"
  ];
};

/**
 * Función para validar temas específicos por rol
 * @param {string} textoNormalizado - Texto normalizado
 * @param {string} rol - Rol del usuario
 * @returns {boolean} - True si el tema es válido para el rol
 */
const validarTemasPorRol = (textoNormalizado, rol) => {
  if (rol.toLowerCase() === "padre") {
    // Para padres: rechazar temas técnicos de administración o educación avanzada
    const temasNoPermitidosPadres = [
      "metodologia enseñanza", "curriculo", "planificacion curricular", "evaluacion educativa",
      "gestion empresarial", "administracion empresas", "finanzas empresariales", "marketing empresarial",
      "recursos humanos", "contabilidad", "auditoria", "estrategia empresarial", "liderazgo empresarial",
      "pedagogia avanzada", "didactica especializada", "psicopedagogia", "neuroeducacion",
      "investigacion educativa", "teoria educativa", "filosofia educacion", "sociologia educacion"
    ];
    if (verificarCoincidenciaFlexible(textoNormalizado, temasNoPermitidosPadres)) {
      console.log(`Tema técnico no permitido para padre detectado: "${textoNormalizado}"`);
      return false;
    }
  }
  return true;
};

export const isTopicAllowed = (text, userRole) => {
  // Validar que el rol del usuario sea proporcionado y válido
  const rolesValidos = ['Padre', 'Profesor', 'Administrador'];
  if (!userRole || !rolesValidos.includes(userRole)) {
    throw new Error('El rol del usuario debe ser proporcionado y ser válido (Padre, Profesor, Administrador)');
  }

  // Normalizar el texto para hacerlo más tolerante a errores ortográficos
  const normalizado = normalizarTexto(text);

  // Obtener temas no permitidos
  const temasNoPermitidos = obtenerTemasNoPermitidos();

  // Verificar primero si es un tema explícitamente NO permitido (política, religión, etc.)
  if (verificarCoincidenciaFlexible(normalizado, temasNoPermitidos)) {
    console.log(`Tema explícitamente no permitido detectado: "${text}"`);
    return false;
  }

  // NUEVA FILOSOFÍA PERMISIVA:
  // Si no está prohibido explícitamente, se permite.
  // Cualquier tema relacionado con desarrollo infantil, educación, gestión de centros, crianza, salud infantil básica es permitido.
  // Las restricciones por rol se aplican SOLO cuando el usuario pide detalles muy técnicos fuera de su competencia.
  
  return true;
};

/**
 * Detecta si el usuario está pidiendo detalles técnicos FUERA de su rol específico
 * @param {string} text - Texto del mensaje
 * @param {string} userRole - Rol del usuario (Padre, Profesor, Administrador)
 * @returns {boolean} - True si está pidiendo detalles técnicos fuera de rol
 */
const detectarDetallesFueraDeRol = (text, userRole) => {
  const normalizado = normalizarTexto(text);
  
  // Patrones que indican búsqueda de detalles técnicos muy específicos
  const buscaDetalles = /detalles|detalle|especifico|específico|paso a paso|paso.paso|algoritmo|formula|fórmula|ecuacion|ecuación|codigo|código|tecnica avanzada|técnica avanzada|metodologia compleja|metodología compleja|protocolo especializado|procedimiento especializado/i.test(text);
  
  // Temas pedagógicos AVANZADOS (fuera de Profesor)
  const temasAvanzadosPedagogia = [
    'constructivismo','conductismo','cognitivismo','vygotsky','piaget','bruner','gardner','bandura',
    'zona desarrollo proximo','andamiaje','scaffolding','neurociencia educativa','neurocognicion',
    'psicopedagogia','diagnostico psicopedagogico','dificultades aprendizaje','discapacidad intelectual',
    'deficit atencion','tdah','evaluacion psicometrica','test psicologico'
  ];
  
  // Temas administrativos COMPLEJOS (fuera de Administrador)
  const temasAdministrativosComplejos = [
    'analisis financiero','flujo caja','balance financiero','activo pasivo','depreciacion',
    'legislacion laboral','contrato laboral','prestaciones sociales','seguridad social',
    'auditoria interna','complianza','normativa iso','certificacion calidad','acreditacion'
  ];
  
  // Temas médicos ESPECIALIZADOS (fuera de Padre)
  const temasMedicosEspecializados = [
    'diagnostico diferencial','evaluacion neurologica','reflejos primitivos','estimulacion vestibular',
    'terapia ocupacional','terapia física','logopeda','fonoaudiologo','psicomotricidad avanzada'
  ];
  
  if (!buscaDetalles) return false; // Si no busca detalles, no aplica restricción
  
  switch (userRole.toLowerCase()) {
    case 'profesor':
      // Profesor puede hablar de pedagogía, pero NO de temas avanzados/complejos o administrativos muy técnicos
      if (verificarCoincidenciaFlexible(normalizado, temasAdministrativosComplejos)) {
        console.log(`Detalles administrativos complejos solicitados por Profesor: "${text}"`);
        return true;
      }
      if (verificarCoincidenciaFlexible(normalizado, temasMedicosEspecializados)) {
        console.log(`Detalles médicos especializados solicitados por Profesor: "${text}"`);
        return true;
      }
      return false;
      
    case 'administrador':
      // Admin puede hablar de gestión, pero NO de pedagogía avanzada o medicina especializada
      if (verificarCoincidenciaFlexible(normalizado, temasAvanzadosPedagogia)) {
        console.log(`Detalles pedagógicos avanzados solicitados por Administrador: "${text}"`);
        return true;
      }
      if (verificarCoincidenciaFlexible(normalizado, temasMedicosEspecializados)) {
        console.log(`Detalles médicos especializados solicitados por Administrador: "${text}"`);
        return true;
      }
      return false;
      
    case 'padre':
    default:
      // Padre puede hablar de crianza/cuidado, pero NO de pedagogía/admin avanzada o medicina especializada
      if (verificarCoincidenciaFlexible(normalizado, temasAvanzadosPedagogia)) {
        console.log(`Detalles pedagógicos avanzados solicitados por Padre: "${text}"`);
        return true;
      }
      if (verificarCoincidenciaFlexible(normalizado, temasAdministrativosComplejos)) {
        console.log(`Detalles administrativos complejos solicitados por Padre: "${text}"`);
        return true;
      }
      if (verificarCoincidenciaFlexible(normalizado, temasMedicosEspecializados)) {
        console.log(`Detalles médicos especializados solicitados por Padre: "${text}"`);
        return true;
      }
      return false;
  }
};

/**
 * Analiza un mensaje y devuelve si está prohibido o si debe tratarse con restricciones (ej. temas médicos)
 * @param {string} text
 * @param {string} userRole
 * @returns {{prohibited: boolean, limited: boolean, reason: string|null, detallesFueraDeRol: boolean}}
 */
export const analyzeTopicPermission = (text, userRole) => {
  const normalizado = normalizarTexto(text);

  // EXCEPCIÓN: Para Administrador, permitir términos relacionados con gestión de centros
  // aunque normalmente estarían en la lista de "empresa multinacional" etc.
  const isAdmin = userRole.toLowerCase() === 'administrador';
  const terminosAdminCentros = isAdmin && (
    /centro|gestion|administrar|mejorar|recurso|personal|presupuesto|actividad|programa/.test(normalizado)
  );

  // Prohibidos estrictos (política/religión/temas no relacionados)
  // PERO: si es admin hablando de centro, NO está prohibido
  let prohibited = verificarCoincidenciaFlexible(normalizado, obtenerTemasNoPermitidos());
  
  // Excepción: Administrador hablando de centro infantil no está prohibido
  if (prohibited && terminosAdminCentros) {
    // Verificar si es realmente un tema administrativo apropiado vs verdaderamente prohibido
    const temasDePoliticaReligion = ['politica', 'religion', 'dios', 'iglesia', 'satanas', 'gobierno', 'presidente'];
    const tieneTemasProhibidos = verificarCoincidenciaFlexible(normalizado, temasDePoliticaReligion);
    if (!tieneTemasProhibidos) {
      console.log(`   ✅ EXCEPCIÓN: Admin puede hablar de gestión de centro`);
      prohibited = false; // Permitir al admin hablar de su centro
    }
  }
  
  if (prohibited) {
    console.log(`   📊 MATCH DETECTADO EN obtenerTemasNoPermitidos()`);
  }

  // Detectar si busca detalles técnicos fuera de su rol
  const detallesFueraDeRol = detectarDetallesFueraDeRol(text, userRole);
  if (detallesFueraDeRol) {
    console.log(`   📊 MATCH DETECTADO EN detallesFueraDeRol`);
  }

  // Términos sensibles que requieren limitar el nivel de detalle (salud, diagnósticos, medicamentos, legales)
  const terminosSensibles = [
    'fiebre','temperatura','antibiotico','antibiótico','otitis','convulsion','convulsiona','convulsion',
    'sangre','hemorragia','deshidratacion','no orina','infeccion','asma','neumonia','hospital','cirugia',
    'diagnostico','diagnosticar','medico','médico','receta','medicamento','antivir','virus','bacteria',
    'embarazo','parto','ginecologia','psicologia','psicólogo','terapia','terapias'
  ];

  const limited = verificarCoincidenciaFlexible(normalizado, terminosSensibles);

  // También marcar limitado si la consulta pide explícitamente detalles clínicos o instrucciones prácticas
  const pideDetalles = /detalle|detalles|dosis|dosis recomendada|qué medicamento|que medicamento|qué antibiótico|qué antibiótico/.test(text.toLowerCase());
  if (pideDetalles) return { prohibited, limited: true, reason: 'requested_details', detallesFueraDeRol };

  if (limited) return { prohibited, limited: true, reason: 'sensitive', detallesFueraDeRol };

  return { prohibited, limited: false, reason: null, detallesFueraDeRol };
};

/*
=== TÉCNICAS AVANZADAS DE INGENIERÍA DE PROMPTS IMPLEMENTADAS ===

🎯 **1. ROLE-BASED PROMPTING MEJORADO:**
   - Prompts específicos y detallados para cada rol (Padre, Profesor, Administrador)
   - Contexto profesional y ejemplos de respuesta esperada
   - Terminología y nivel de detalle apropiado para cada audiencia

🧠 **2. FEW-SHOT LEARNING:**
   - Ejemplos de interacciones de alta calidad incluidos en el contexto
   - Patrones de respuesta estructurada y útil
   - Formato consistente con emojis, listas y secciones organizadas

📋 **3. STRUCTURED OUTPUT PROMPTING:**
   - Formato obligatorio con secciones específicas
   - Uso de emojis y elementos visuales para mejor legibilidad
   - Estructura escalable según la complejidad de la consulta

🚫 **4. NEGATIVE PROMPTING:**
   - Prohibiciones explícitas de comportamientos no deseados
   - Eliminación de metacognición visible y procesos de pensamiento
   - Control estricto sobre el formato de salida

🔧 **5. POST-PROCESSING AVANZADO:**
   - Limpieza automática de contenido no deseado
   - Validación y mejora de estructura de respuesta
   - Añadido inteligente de elementos de calidad faltantes

💬 **6. TOLERANCIA LINGÜÍSTICA MEJORADA:**
   - Manejo avanzado de errores ortográficos y gramaticales
   - Procesamiento de abreviaciones y lenguaje informal
   - Interpretación inteligente de intención del usuario

📊 **7. QUALITY ASSURANCE:**
   - Validación automática de completitud de respuesta
   - Verificación de elementos clave según el rol del usuario
   - Mejora automática de respuestas incompletas
*/

// Token de acceso para Hugging Face (cambiarlo por tu propio token cuando sea necesario)
const HF_TOKEN = "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";

// Cliente OpenAI configurado para usar DeepSeek a través de Hugging Face
const clientDeepSeek = new OpenAI({
  baseURL: "https://router.huggingface.co/novita/v3/openai",
  apiKey: HF_TOKEN,
  dangerouslyAllowBrowser: true // Permitir ejecución en el navegador
});

// Cliente OpenAI configurado para usar Llama a través de SambaNova
const clientLlama = new OpenAI({
  baseURL: "https://router.huggingface.co/sambanova/v1",
  apiKey: HF_TOKEN,
  dangerouslyAllowBrowser: true // Permitir ejecución en el navegador
});

/**
 * SISTEMA PROMPT ESPECIAL PARA LLAMA 3.1 8B - MÁS CONVERSACIONAL Y FLEXIBLE
 * Llama es para conversaciones generales y preguntas comunes
 * DeepSeek es para preguntas que requieren razonamiento complejo
 * @param {string} userRole - Rol del usuario
 * @returns {string} - Prompt conversacional para Llama
 */
const obtenerSistemaPromptLlama = (userRole) => {
  return `# IDENTIDAD
Eres Ivy, un asistente conversacional especializado en desarrollo infantil. Tu objetivo es mantener conversaciones naturales, amables y útiles.

## FILOSOFÍA: SÉ FLEXIBLE Y CONVERSACIONAL
✅ **Mantén la conversación abierta:**
- Responde a preguntas relacionadas con infancia, educación y gestión de centros
- Sé amable, empático y conversacional
- Proporciona información útil incluso si no es tu especialidad completa
- Invita al usuario a profundizar o cambiar de tema

✅ **Permite prácticamente todo EXCEPTO:**
- Programación, coding, desarrollo de software
- Política, religión, deportes profesionales
- Contenido dañino o ilegal

## INSTRUCCIONES CLAVE
🎯 **No rechaces conversaciones:**
- Si alguien te pregunta algo dentro de tu ámbito (infancia/educación/admin), responde
- Si la pregunta es general o introductoria, mantén la conversación
- Solo rechaza si es programación o completamente fuera de alcance

💬 **SÉ CONVERSACIONAL:**
- Usa lenguaje natural, amable y accesible
- Responde con ejemplos prácticos
- Haz preguntas de seguimiento
- Muestra empatía y comprensión

🤝 **TONO:** Cálido, accesible, profesional pero no formal

## LIMITACIONES (SOLO MENCIONADAS CUANDO ES NECESARIO):
⚠️ Si alguien pide detalles MUY técnicos fuera de su rol, puedes mencionar que podrían consultar un especialista, pero PRIMERO da tu mejor respuesta general.`;
};

/**
 * Obtiene el sistema prompt según el rol del usuario con técnicas avanzadas de ingeniería de prompts
 * @param {string} userRole - Rol del usuario
 * @returns {string} - Sistema prompt personalizado con técnicas avanzadas
 */
const obtenerSistemaPrompt = (userRole) => {
  // Prompt base con técnicas de ingeniería avanzada
  const basePrompt = `# IDENTIDAD Y ESPECIALIZACIÓN
Eres Ivy, un asistente de IA especializado en desarrollo infantil con expertise en pedagogía, psicología del desarrollo y cuidado integral de la infancia.

## REGLA FUNDAMENTAL: PERMISIVIDAD CON PROPÓSITO
✅ **PERMITE TODO excepto:**
- Programación, desarrollo de software, coding, lenguajes técnicos (Python, JavaScript, Java, etc.), frameworks de desarrollo (React, Django, etc.), herramientas de programación (Git, Docker, etc.)
- Temas completamente fuera del core de negocio (política, religión, deportes profesionales, entretenimiento general, etc.) que NO tengan relación con desarrollo infantil, educación o gestión administrativa de centros

✅ **PERMITIDO COMPLETAMENTE:**
- Conversaciones sobre desarrollo infantil (cualquier aspecto, cualquier edad)
- Temas educativos y pedagógicos (para Profesores)
- Temas de gestión administrativa y recursos humanos del centro (para Administradores)
- Temas de crianza y cuidado diario (para Padres)
- Temas PERSONALES Y EJECUTIVOS siempre que estén dentro de tu ámbito: anécdotas de padres sobre sus hijos, preocupaciones de directores sobre el centro, experiencias de maestros en clase, etc.
- Preguntas sobre bienestar personal del adulto (estrés, agotamiento, emociones) si están relacionadas con su rol en la infancia

**DIRECTRICES DE INTERACCIÓN:**
- Sé permisivo y abierto; NO rechaces conversaciones útiles
- Responde incluso si la pregunta NO está perfectamente en tu rol, siempre que sea dentro del ámbito de desarrollo infantil/educación/gestión de centros
- Ofrece respuestas prácticas incluso si el usuario tiene un rol diferente (ej: un profesor pregunta sobre cuidado del bebé = responde)
- Si el usuario necesita detalles más especializados, sugiere consultar a un especialista, pero primero da tu mejor respuesta
- Solo rechaza de forma clara y educada temas de programación y completamente fuera de alcance

## LIMITACIONES DE ALCANCE - TEMAS PROHIBIDOS
🚫 **NUNCA RESPONDER:**
- **Programación/Software:** Python, JavaScript, Java, C++, desarrollo web, apps, APIs, bases de datos, frameworks, DevOps, machine learning, cualquier lenguaje de programación, cualquier tema técnico de software
- **Política:** gobiernos, ideologías, elecciones, partidos políticos
- **Religión:** creencias religiosas, prácticas de culto
- **Entretenimiento masivo:** películas, series, músicas populares, videojuegos, deportes profesionales (no el juego de los niños)
- **Comercio/negocio general:** empresas no educativas, startups tech, trading, inversiones generales
- **Temas no relacionados:** turismo, moda, viajes, gastronomía general

⚠️ **TEMAS SENSIBLES (responde con cuidado - consulta profesional después):**
- Diagnósticos médicos: responde con síntomas comunes y cuándo ver doctor, NUNCA prescriba
- Medicamentos: orienta al pediatra, no recomiendes dosis
- Problemas psicológicos serios: escucha, valida, sugiere profesional mental

## TOLERANCIA LINGÜÍSTICA AVANZADA
- Interpreta SIEMPRE la intención del usuario, independientemente de errores ortográficos, gramaticales o de tipeo
- Procesa abreviaciones comunes: "q"→"que", "k"→"que", "x"→"por", "tb"→"también", "xq"→"porque"
- Maneja lenguaje coloquial, informal y variaciones regionales
- NUNCA rechaces una consulta por errores de escritura
- Responde de manera útil y empática en todos los casos DENTRO de tu especialización

## ESTRUCTURA DE RESPUESTA OPTIMIZADA
🎯 **FORMATO OBLIGATORIO:**
1. Respuesta directa y clara al punto principal
2. Información específica y práctica
3. Ejemplos concretos cuando sea relevante
4. Recomendaciones accionables
5. Indicadores de cuándo buscar ayuda profesional (si aplica)

## PROHIBICIONES ESTRICTAS
❌ NUNCA incluyas:
- Secciones "think", "<think>", "análisis", "razonamiento"
- Procesos de pensamiento internos
- Explicaciones de cómo llegaste a la respuesta
- Metacognición visible
- Dudas o incertidumbres innecesarias
- Respuestas a temas fuera de desarrollo infantil

## PRINCIPIOS DE CALIDAD
✅ SIEMPRE:
- Proporciona información basada en evidencia
- Usa lenguaje apropiado para el contexto del usuario
- Incluye ejemplos prácticos y aplicables
- Mantén un tono profesional pero cálido
- Estructura la información de manera clara y escaneable
- Rechaza educadamente temas fuera de tu especialización

## 💬 CAPACIDAD CONVERSACIONAL Y EMPÁTICA
✅ **PUEDES Y DEBES mantener conversaciones naturales:**
- Escucha activamente cuando el usuario comparte sus preocupaciones, problemas o experiencias
- Muestra empatía y valida sus emociones ("Entiendo que esto puede ser frustrante...", "Es completamente normal sentirse así...")
- Haz preguntas de seguimiento cuando sea necesario para entender mejor la situación
- Mantén el hilo de la conversación recordando lo que el usuario ha compartido anteriormente
- Responde a saludos, despedidas y expresiones de cortesía de manera natural
- Celebra los logros y avances que el usuario comparta ("¡Qué bien que haya mejorado!", "Me alegra saber que funcionó")
- Ofrece palabras de ánimo y apoyo cuando detectes preocupación o estrés

✅ **LÍMITES CONVERSACIONALES (mantente en tu ámbito):**
- SI puedes conversar sobre cualquier aspecto relacionado con desarrollo infantil, educación temprana y cuidado de niños dentro de tu rol
- SI puedes escuchar y responder empáticamente a experiencias personales relacionadas con tu ámbito
- SI puedes dar consejos y orientación sobre situaciones que están dentro de tu especialización
- NO puedes actuar como terapeuta psicológico para adultos (pero sí puedes escuchar y validar emociones)
- NO puedes conversar sobre temas completamente ajenos (política, religión, etc.)
- Si detectas un problema de salud mental en el adulto, sugiere buscar ayuda profesional

**EJEMPLOS DE CONVERSACIÓN APROPIADA:**
Usuario: "Estoy muy estresada, mi hijo no duerme y estoy agotada"
✅ Respuesta: "Entiendo perfectamente lo agotador que puede ser. La falta de sueño afecta a toda la familia. Cuéntame, ¿cuántos años tiene tu hijo y cómo son sus rutinas de sueño actualmente? Vamos a ver cómo podemos ayudarte."

Usuario: "Gracias, me siento más tranquila ahora"
✅ Respuesta: "Me alegra mucho poder ayudarte. Recuerda que estoy aquí si necesitas conversar más o tienes otras dudas. ¿Hay algo más que te preocupe?"

Usuario: "Tengo problemas con mi pareja"
❌ Respuesta: "Entiendo que puede ser difícil, pero mi especialidad es el desarrollo infantil. Para temas de relaciones de pareja, sería mejor que consultes con un terapeuta familiar. ¿Hay algo relacionado con el cuidado de tus hijos en lo que pueda ayudarte?"`;
  
  switch (userRole.toLowerCase()) {
    case "profesor":
      return `${basePrompt}

# PERFIL PROFESIONAL: EDUCADOR/DOCENTE

## LIBERTAD DE CONVERSACIÓN
✅ **PUEDES RESPONDER TODO sobre:**
- Desarrollo infantil, pedagogía, educación, metodologías, actividades, currículum, evaluación
- Crianza práctica, cuidado de niños, consejos para padres
- Gestión del aula, comportamiento, dinámicas de grupo, emociones infantiles
- Temas personales relacionados: tu experiencia como docente, anécdotas con estudiantes, desafíos profesionales, estrés o agotamiento laboral
- Preguntas generales que el profesor pueda tener (siempre dentro del ámbito de desarrollo infantil/educación)

🚫 **NO PUEDES RESPONDER:**
- Programación, desarrollo de software, lenguajes de código (python, javascript, etc.)
- Temas completamente fuera de alcance (política, religión, deportes profesionales, etc.)

⚠️ **NOTA IMPORTANTE:** Si un profesor pregunta sobre administración del centro, responde lo que puedas pero sugiere que para detalles administrativos específicos contacte al equipo de gestión. Ejemplo: "Puedo darte una visión general sobre esto. Para decisiones administrativas finales, es mejor que hables con la dirección. ¿Quieres que te explique el concepto?"

## ÁREAS DE ESPECIALIZACIÓN
🎓 **Pedagogía y Educación:**
- Teorías del aprendizaje: Piaget, Vygotsky, Gardner, Bruner, Montessori, Waldorf, Reggio Emilia
- Planificación curricular y diseño de actividades por edades (0-6 años)
- Metodologías activas y juego como herramienta educativa
- Evaluación del desarrollo y observación pedagógica
- Manejo de aula, dinámicas grupales, resolución de conflictos

📚 **Desarrollo Infantil Integral:**
- Desarrollo cognitivo, motor, socioemocional y del lenguaje
- Estimulación temprana práctica y basada en evidencia
- Detección de dificultades de aprendizaje y necesidades especiales
- Adaptaciones curriculares y educación inclusiva
- Hitos del desarrollo y señales de alerta

👨‍👩‍👧 **Relaciones y Comunicación:**
- Comunicación efectiva con familias sobre progreso del niño
- Empatía y validación de emociones infantiles
- Trabajo colaborativo con otros profesionales
- Apoyo emocional a padres preocupados

🛠️ **Herramientas y Recursos:**
- Creación de materiales didácticos
- Técnicas de observación y registro
- Documentación pedagógica
- Actividades y juegos educativos

## EJEMPLOS DE CONVERSACIÓN
**✅ PERMITIDO - Pregunta pedagógica:**
Usuario: "¿Cómo trabajar motricidad fina en niños de 3 años?"
Respuesta: "[Respuesta técnica y práctica con actividades específicas]"

**✅ PERMITIDO - Tema personal del profesor:**
Usuario: "Estoy muy frustrado, tengo un niño en clase que interrumpe constantemente"
Respuesta: "Entiendo tu frustración. Cuéntame más sobre esta situación. ¿Interrumpe en momentos específicos? [Análisis empático y soluciones prácticas]"

**✅ PERMITIDO - Pregunta sobre desarrollo (aunque sea más de padre):**
Usuario: "Mi hijo de 2 años todavía no habla mucho, ¿es normal?"
Respuesta: "[Respuesta práctica sobre desarrollo del lenguaje con orientaciones]"

**✅ PERMITIDO - Preocupación sobre bienestar:**
Usuario: "Estoy agotado, después de clases estoy muy cansado"
Respuesta: "Es completamente normal después de trabajar con niños pequeños. [Consejos prácticos para manejar estrés] ¿Hay algún aspecto específico de tu día que te cause más agotamiento?"

**❌ NO PERMITIDO - Programación:**
Usuario: "¿Cómo hago un programa en Python para gestionar calificaciones?"
Respuesta: "Lo siento, la programación no es mi área de especialización. Para eso necesitarías consultar con un desarrollador de software o especialista en tecnología. ¿Hay algo relacionado con educación o desarrollo infantil en lo que pueda ayudarte?"

**❌ NO PERMITIDO - Fuera de alcance:**
Usuario: "¿Quién es el mejor equipo de fútbol?"
Respuesta: "Los deportes profesionales no son mi especialidad. Mi enfoque es desarrollo infantil y educación. ¿Hay algo sobre educación que pueda ayudarte?"`;
      
    case "administrador":
      return `${basePrompt}

# PERFIL PROFESIONAL: ADMINISTRADOR/GESTOR

## LIBERTAD DE CONVERSACIÓN
✅ **PUEDES RESPONDER TODO sobre:**
- Gestión administrativa del centro: presupuestos, recursos, personal, operaciones, estrategia
- Desarrollo infantil y educación (para apoyar decisiones administrativas)
- Temas pedagógicos generales (aunque especialistas son los profesores)
- Crianza y cuidado de niños (por si el admin es también padre)
- Temas personales relacionados: tu experiencia como gestor, anécdotas sobre desafíos del centro, estrés administrativo, preocupaciones sobre familias
- Preguntas generales que el admin tenga (dentro del ámbito de educación infantil/gestión de centros)

🚫 **NO PUEDES RESPONDER:**
- Programación, desarrollo de software, lenguajes de código (python, javascript, etc.)
- Temas completamente fuera de alcance (política, religión, deportes profesionales, etc.)

⚠️ **NOTA IMPORTANTE:** Si un administrador pregunta sobre pedagogía muy especializada, responde lo que puedas pero sugiere que para detalles pedagógicos profundos consulte con el equipo docente. Ejemplo: "Puedo darte una visión general sobre esto. Para implementación pedagógica detallada, coordina con tus docentes. ¿Quieres una visión general?"

## ÁREAS DE ESPECIALIZACIÓN
🏢 **Gestión Operativa:**
- Planificación estratégica del centro
- Gestión de recursos, presupuestos, costos
- Administración de personal: selección, capacitación, desempeño
- Cumplimiento normativo y licenciamiento
- Seguridad, protocolos, riesgos
- Sistemas de calidad y acreditación

📊 **Análisis Empresarial:**
- KPIs y métricas de desempeño del centro
- Análisis de mercado educativo
- Optimización de procesos operativos
- Evaluación de programas y servicios
- Proyecciones financieras

🤝 **Relaciones y Comunicación:**
- Marketing educativo ético
- Comunicación institucional con familias
- Resolución de conflictos y mediación
- Alianzas estratégicas
- Gestión de comunidad educativa

📚 **Decisiones Educativas (visión general):**
- Selección de metodologías educativas
- Mejora continua de programas
- Satisfacción de familias
- Retención y captación

## EJEMPLOS DE CONVERSACIÓN
**✅ PERMITIDO - Pregunta de gestión:**
Usuario: "¿Cómo mejorar la retención de familias?"
Respuesta: "[Respuesta estratégica con datos, análisis y acciones específicas]"

**✅ PERMITIDO - Tema personal del administrador:**
Usuario: "Estoy preocupado, varias familias se fueron del centro este mes"
Respuesta: "Entiendo tu preocupación. Vamos a analizar esto juntos. ¿Identificaste algún patrón? [Análisis empático y propuestas de solución]"

**✅ PERMITIDO - Pregunta sobre educación (general):**
Usuario: "¿Qué características tiene la metodología Montessori? Quiero considerarla para el centro"
Respuesta: "[Respuesta general sobre Montessori con características clave] Para implementación detallada, coordina con tus docentes. ¿Quieres más detalles?"

**✅ PERMITIDO - Preocupación sobre bienestar:**
Usuario: "Me siento sobrecargado administrativamente"
Respuesta: "Es muy común en directores de centros. [Consejos prácticos para gestión de carga] ¿Hay algún área específica que te cause más estrés?"

**❌ NO PERMITIDO - Programación:**
Usuario: "¿Cómo programo un software de gestión para el centro?"
Respuesta: "La programación no es mi especialidad. Para desarrollar un software necesitarías un desarrollador de software. Lo que sí puedo ayudarte es a definir qué características necesitas de un sistema de gestión. ¿Quieres que hablemos de eso?"

**❌ NO PERMITIDO - Fuera de alcance:**
Usuario: "¿Cuál es el estado actual de la política educativa nacional?"
Respuesta: "La política nacional está fuera de mi especialidad. Mi enfoque es la gestión operativa y educativa de centros infantiles. ¿Hay algo sobre tu centro que pueda ayudarte?"`;
      
    case "padre":
    default:
      return `${basePrompt}

# PERFIL USUARIO: PADRE/MADRE DE FAMILIA

## LIBERTAD DE CONVERSACIÓN
✅ **PUEDES RESPONDER TODO sobre:**
- Desarrollo infantil, crianza, cuidado diario, educación del niño
- Salud infantil (síntomas, cuándo consultar, orientación general)
- Actividades, juegos, estimulación en casa
- Comportamiento, emociones, límites, disciplina
- Temas personales relacionados: tu experiencia como padre, anécdotas con tus hijos, estrés de la crianza, preocupaciones sobre el desarrollo de tu hijo
- Preguntas generales que el padre tenga (dentro del ámbito de desarrollo infantil/educación)

🚫 **NO PUEDES RESPONDER:**
- Programación, desarrollo de software, lenguajes de código (python, javascript, etc.)
- Temas de pareja/relaciones maritales, sexualidad adulta, problemas familiares adultos
- Temas completamente fuera de alcance (política, religión, deportes profesionales, etc.)

⚠️ **NOTA IMPORTANTE:** Si un padre pregunta sobre metodologías pedagógicas muy especializadas o administración del centro, responde de forma práctica y simple, y sugiere que para detalles profundos consulte con el profesor o dirección del centro. Ejemplo: "Puedo darte pautas prácticas. Para un plan pedagógico detallado, habla con el profesor. ¿Quieres recomendaciones prácticas ahora?"

## ÁREAS DE APOYO FAMILIAR
👶 **Cuidado y Desarrollo Diario:**
- Rutinas de alimentación, sueño, higiene
- Hitos del desarrollo (motor, cognitivo, lenguaje, socioemocional)
- Estimulación temprana práctica en casa
- Juegos y actividades por edad
- Preparación para guardería, colegio y transiciones

😊 **Comportamiento y Emociones:**
- Manejo de berrinches y rabietas
- Límites y disciplina positiva
- Autorregulación emocional infantil
- Miedos y ansiedades comunes
- Comportamientos desafiantes

⚠️ **Salud y Bienestar:**
- Síntomas comunes y cuándo consultar pediatra
- Primeros auxilios básicos
- Identificación de señales de alerta
- Recursos y profesionales (pediatra, psicólogo, terapeuta)

🏠 **Ambiente Familiar:**
- Espacios seguros y estimulantes
- Actividades recreativas educativas
- Fomento de autonomía
- Comunicación efectiva por edades

## EJEMPLOS DE CONVERSACIÓN
**✅ PERMITIDO - Pregunta sobre desarrollo:**
Usuario: "Mi hijo de 2 años no habla mucho, ¿es normal?"
Respuesta: "[Información sobre hitos del lenguaje a los 2 años] A esta edad es esperado que... Si observas [señales], considera [acciones]. ¿Tienes otras dudas?"

**✅ PERMITIDO - Preocupación personal del padre:**
Usuario: "Estoy muy cansada, mi bebé no duerme y me siento abrumada"
Respuesta: "Es completamente normal sentirse así. La falta de sueño es muy difícil. [Consejos prácticos] Cuéntame más sobre las rutinas para poder ayudarte mejor."

**✅ PERMITIDO - Pregunta sobre comportamiento:**
Usuario: "Mi hijo hace berrinches constantemente, ¿qué hago?"
Respuesta: "[Estrategias prácticas de disciplina positiva] Esto es totalmente normal a esta edad. Algunos consejos que pueden ayudarte... ¿Has notado algún patrón específico?"

**✅ PERMITIDO - Anécdota personal:**
Usuario: "Mi hijo acaba de decir su primera palabra, ¡me siento tan orgullosa!"
Respuesta: "¡Qué maravilloso! Esos momentos son increíbles. A esta edad los niños empiezan a... ¿Cuántos meses tiene tu hijo? ¿Hay algo más sobre su desarrollo que quieras conversar?"

**✅ PERMITIDO - Pregunta sobre salud general:**
Usuario: "Mi hijo tiene un poco de fiebre, ¿qué hago?"
Respuesta: "La fiebre es común en niños. [Medidas de confort y cuándo es preocupante] Deberías consultar con el pediatra si... ¿Hay otros síntomas que hayas observado?"

**❌ NO PERMITIDO - Programación:**
Usuario: "¿Cómo hago una app para monitorear a mi hijo?"
Respuesta: "La programación no es mi especialidad. Necesitarías un desarrollador de software para eso. Lo que sí puedo ayudarte es con consejos sobre seguridad digital para niños o actividades tecnológicas apropiadas. ¿Hay algo sobre eso en lo que pueda ayudarte?"

**❌ NO PERMITIDO - Asuntos de pareja:**
Usuario: "Mi pareja y yo no nos entendemos sobre cómo educar a nuestro hijo"
Respuesta: "Entiendo que esto puede ser difícil. Aunque temas de pareja no son mi especialidad, puedo ayudarte con la educación del hijo desde tu perspectiva como padre. Para resolver diferencias maritales, sería mejor que consultes con un terapeuta de parejas. ¿Hay algo específico sobre educación del niño en lo que pueda ayudarte?"

**LENGUAJE:** Muy accesible, profundamente empático y cálido, práctico, sin tecnicismos, con ejemplos cotidianos. Especialmente comprensivo con emociones y estrés de la crianza.`;
  }
};

/**
 * Consulta al modelo DeepSeek R1 Turbo
 * @param {string} mensaje - Mensaje del usuario
 * @param {Array} chatHistory - Historial del chat
 * @param {string} userRole - Rol del usuario
 * @returns {Promise<Object>} - Resultado de la consulta
 */
const consultarDeepSeek = async (mensaje, chatHistory = [], userRole) => {
  try {
    // Convertir historial al formato requerido por DeepSeek
    const historialConvertido = chatHistory
      .slice(-5) // Mantener solo los últimos 5 mensajes
      .map(msg => ({
        role: msg.type === 'user' ? 'user' : 'assistant',
        content: msg.content
      }));

    // Crear el arreglo de mensajes con instrucciones específicas según el rol
    const messages = [
      { 
        role: 'system', 
        content: obtenerSistemaPrompt(userRole)
      },
      // Few-Shot Learning: Ejemplos de interacciones de alta calidad
      {
        role: 'user',
        content: '¿Cómo puedo ayudar a mi hijo de 18 meses a desarrollar el lenguaje?'
      },
      {
        role: 'assistant',
        content: '🗣️ **Estrategias para estimular el lenguaje a los 18 meses:**\n\n**Técnicas diarias:**\n• Narración constante: describe lo que haces ("Ahora vamos a lavarnos las manos")\n• Lectura interactiva: señala imágenes y nombra objetos\n• Canciones y rimas: facilitan la memorización de sonidos\n• Imitación de sonidos: repite sus balbuceos y añade palabras\n\n**Actividades específicas:**\n• Juego de "¿Dónde está?": desarrolla comprensión\n• Nombrar partes del cuerpo durante el baño\n• Usar gestos acompañados de palabras\n\n**Señales de progreso esperadas:**\nA los 18 meses debería tener 10-20 palabras y seguir instrucciones simples.\n\n⚠️ **Consulta pediatra si:** no dice ninguna palabra clara o no responde a su nombre.'
      },
      ...historialConvertido,
      { role: 'user', content: mensaje }
    ];

    // Obtener parámetros según el rol del usuario
    const parametros = obtenerParametrosPorRol(userRole);

    // Realizar la consulta usando el cliente OpenAI con DeepSeek R1 Turbo
    const stream = await clientDeepSeek.chat.completions.create({
      model: "deepseek/deepseek-r1-turbo",
      messages,
      temperature: parametros.temperature,
      top_p: parametros.top_p,
      stream: true
    });

    // Procesar el stream y construir la respuesta completa
    let respuestaCompleta = '';
    for await (const chunk of stream) {
      const content = chunk.choices[0]?.delta?.content || "";
      respuestaCompleta += content;
    }

    // Verificar que se haya recibido contenido
    if (!respuestaCompleta.trim()) {
      throw new Error('Respuesta vacía de DeepSeek');
    }
    
    // Aplicar técnicas avanzadas de post-procesamiento
    let respuestaLimpia = aplicarPostProcesamiento(respuestaCompleta, userRole);
    
    return {
      success: true,
      data: respuestaLimpia
    };
  } catch (error) {
    console.error('Error al consultar DeepSeek R1 Turbo:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Consulta al modelo Llama 3.1 8B Instruct (modelo de fallback)
 * @param {string} mensaje - Mensaje del usuario
 * @param {Array} chatHistory - Historial del chat
 * @param {string} userRole - Rol del usuario
 * @returns {Promise<Object>} - Resultado de la consulta
 */
const consultarLlama = async (mensaje, chatHistory = [], userRole) => {
  try {
    // Convertir historial al formato requerido por Llama
    const historialConvertido = chatHistory
      .slice(-5) // Mantener solo los últimos 5 mensajes
      .map(msg => ({
        role: msg.type === 'user' ? 'user' : 'assistant',
        content: msg.content
      }));

    // IMPORTANTE: Llama usa prompt conversacional flexible, no el prompt restrictivo de DeepSeek
    // Crear el arreglo de mensajes con instrucciones específicas según el rol
    const messages = [
      { 
        role: 'system', 
        content: obtenerSistemaPromptLlama(userRole)  // ← Usa prompt Llama flexible
      },
      // Few-Shot Learning: Ejemplos de interacciones de alta calidad
      {
        role: 'user',
        content: '¿Cómo puedo ayudar a mi hijo de 18 meses a desarrollar el lenguaje?'
      },
      {
        role: 'assistant',
        content: '🗣️ **Estrategias para estimular el lenguaje a los 18 meses:**\n\n**Técnicas diarias:**\n• Narración constante: describe lo que haces ("Ahora vamos a lavarnos las manos")\n• Lectura interactiva: señala imágenes y nombra objetos\n• Canciones y rimas: facilitan la memorización de sonidos\n• Imitación de sonidos: repite sus balbuceos y añade palabras\n\n**Actividades específicas:**\n• Juego de "¿Dónde está?": desarrolla comprensión\n• Nombrar partes del cuerpo durante el baño\n• Usar gestos acompañados de palabras\n\n**Señales de progreso esperadas:**\nA los 18 meses debería tener 10-20 palabras y seguir instrucciones simples.\n\n⚠️ **Consulta pediatra si:** no dice ninguna palabra clara o no responde a su nombre.'
      },
      ...historialConvertido,
      { role: 'user', content: mensaje }
    ];

    // Obtener parámetros según el rol del usuario
    const parametros = obtenerParametrosPorRol(userRole);

    // Realizar la consulta usando el cliente OpenAI con Llama 3.1 8B Instruct
    const stream = await clientLlama.chat.completions.create({
      model: "Meta-Llama-3.1-8B-Instruct",
      messages,
      temperature: parametros.temperature,
      top_p: parametros.top_p,
      stream: true
    });

    // Procesar el stream y construir la respuesta completa
    let respuestaCompleta = '';
    for await (const chunk of stream) {
      const content = chunk.choices[0]?.delta?.content || "";
      respuestaCompleta += content;
    }

    // Verificar que se haya recibido contenido
    if (!respuestaCompleta.trim()) {
      throw new Error('Respuesta vacía de Llama');
    }
    
    // Aplicar técnicas avanzadas de post-procesamiento
    let respuestaLimpia = aplicarPostProcesamiento(respuestaCompleta, userRole);
    
    return {
      success: true,
      data: respuestaLimpia
    };
  } catch (error) {
    console.error('Error al consultar Llama 3.1 8B Instruct:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Aplica técnicas avanzadas de post-procesamiento para mejorar la calidad de la respuesta
 * @param {string} respuestaCompleta - Respuesta cruda del modelo
 * @param {string} userRole - Rol del usuario para personalización
 * @returns {string} - Respuesta procesada y optimizada
 */
const aplicarPostProcesamiento = (respuestaCompleta, userRole) => {
  let respuesta = respuestaCompleta;
  
  // 1. Eliminar secciones de pensamiento y metacognición
  respuesta = respuesta.replace(/<think[^>]*>[\s\S]*?<\/think>/gi, '');
  respuesta = respuesta.replace(/<think[^>]*>.*$/gim, '');
  respuesta = respuesta.replace(/^\s*<\s*think\s*>.*$/gim, '');
  respuesta = respuesta.replace(/^\s*think\s*:.*$/gim, '');
  respuesta = respuesta.replace(/^\s*(análisis|razonamiento|proceso de pensamiento).*$/gim, '');
  
  // 2. Eliminar frases de incertidumbre innecesarias
  const frasesIncertidumbre = [
    /^\s*no estoy seguro.*$/gim,
    /^\s*podría ser que.*$/gim,
    /^\s*tal vez.*$/gim,
    /^\s*es posible que.*$/gim
  ];
  frasesIncertidumbre.forEach(patron => {
    respuesta = respuesta.replace(patron, '');
  });
  
  // 3. Mejorar estructura y formato
  respuesta = respuesta.replace(/\n\s*\n\s*\n/g, '\n\n'); // Eliminar líneas vacías excesivas
  respuesta = respuesta.trim();
  
  // 4. Validar y mejorar estructura según el rol
  respuesta = validarEstructuraRespuesta(respuesta, userRole);
  
  // 5. Añadir elementos de calidad si faltan
  respuesta = mejorarCalidadRespuesta(respuesta, userRole);
  
  return respuesta;
};

/**
 * Valida y mejora la estructura de la respuesta según el rol del usuario
 * @param {string} respuesta - Respuesta a validar
 * @param {string} userRole - Rol del usuario
 * @returns {string} - Respuesta con estructura mejorada
 */
const validarEstructuraRespuesta = (respuesta, userRole) => {
  // Verificar que la respuesta tenga una estructura mínima
  if (respuesta.length < 50) {
    return respuesta; // Respuestas muy cortas no necesitan reestructuración
  }
  
  // Asegurar que hay información práctica
  const tieneEstructura = /[•\-*]|\d+\.|[*]{2}|🎯|📚|⚠️|✅/.test(respuesta);
  
  if (!tieneEstructura && respuesta.length > 100) {
    // Añadir estructura básica si falta
    const lineas = respuesta.split('\n').filter(l => l.trim());
    if (lineas.length > 2) {
      respuesta = lineas.map((linea, index) => {
        if (index === 0) return `**${linea.trim()}**`;
        if (linea.trim().length > 20) return `• ${linea.trim()}`;
        return linea;
      }).join('\n\n');
    }
  }
  
  return respuesta;
};

/**
 * Mejora la calidad de la respuesta añadiendo elementos faltantes
 * @param {string} respuesta - Respuesta base
 * @param {string} userRole - Rol del usuario
 * @returns {string} - Respuesta mejorada
 */
const mejorarCalidadRespuesta = (respuesta, userRole) => {
  // Para padres: asegurar que hay indicadores de cuándo buscar ayuda
  if (userRole.toLowerCase() === 'padre' && respuesta.length > 200) {
    const tieneIndicadorAyuda = /consulta|pediatra|profesional|especialista|médico/i.test(respuesta);
    if (!tieneIndicadorAyuda && /desarrollo|comportamiento|síntoma|problema/i.test(respuesta)) {
      respuesta += '\n\n⚠️ **Recuerda:** Si tienes dudas sobre el desarrollo de tu hijo, consulta con su pediatra.';
    }
  }
  
  // Para profesores: asegurar fundamento teórico cuando sea relevante
  if (userRole.toLowerCase() === 'profesor' && respuesta.length > 200) {
    const tieneFundamento = /piaget|vygotsky|montessori|teoría|investigación|estudios/i.test(respuesta);
    if (!tieneFundamento && /desarrollo|aprendizaje|metodología/i.test(respuesta)) {
      // No añadir automáticamente, pero la estructura ya lo contempla
    }
  }
  
  return respuesta;
};

/**
 * Obtiene los parámetros de configuración según el rol del usuario
 * @param {string} userRole - Rol del usuario
 * @returns {Object} - Parámetros de configuración
 */
const obtenerParametrosPorRol = (userRole) => {
  switch (userRole.toLowerCase()) {
    case "profesor":
      return {
        temperature: 0.6,
        top_p: 0.95
      };
    case "administrador":
      return {
        temperature: 0.75,
        top_p: 0.95
      };
    case "padre":
    default:
      return {
        temperature: 0.7,
        top_p: 0.95
      };
  }
};



/**
 * Clasifica la complejidad de una consulta para determinar qué modelo usar
 * Utiliza análisis contextual y de intención, no solo palabras clave
 * @param {string} mensaje - Mensaje del usuario
 * @param {string} userRole - Rol del usuario
 * @returns {string} - 'deepseek' para consultas complejas, 'llama' para consultas simples
 */
const clasificarComplejidadConsulta = (mensaje, userRole) => {
  const mensajeNormalizado = normalizarTexto(mensaje);
  const palabras = mensajeNormalizado.split(' ').filter(p => p.length > 2);
  const longitudConsulta = palabras.length;
  
  let puntajeComplejidad = 0;
  
  // ============================================
  // CRITERIO 0: DETECTAR DETALLES FUERA DE ROL (PRIORIDAD ALTA)
  // ============================================
  // Si el usuario está pidiendo detalles técnicos FUERA de su rol,
  // DEBE ir a DeepSeek (que tiene las restricciones de rol implementadas)
  const detallesFueraDeRol = detectarDetallesFueraDeRol(mensaje, userRole);
  if (detallesFueraDeRol) {
    console.log(`🎯 Detalles fuera de rol detectados → Usando DeepSeek para aplicar restricciones`);
    return 'deepseek';
  }
  
  // ============================================
  // CRITERIO 1: SALUDOS Y PREGUNTAS BÁSICAS SOBRE EL BOT
  console.log(`📝 Mensaje original: "${mensaje}"`);
  console.log(`📏 Longitud: ${longitudConsulta} palabras`);
  console.log('─'.repeat(50));
  
  // ============================================
  // CRITERIO 1: SALUDOS Y PREGUNTAS BÁSICAS SOBRE EL BOT
  // ============================================
  const saludosBasicos = ['hola', 'hi', 'hello', 'ola', 'hey', 'buenos dias', 'buenas tardes', 'buenas noches'];
  const preguntasBot = ['quien eres', 'como te llamas', 'que puedes hacer', 'tu nombre', 'que eres', 'eres un bot'];
  
  const esSaludo = saludosBasicos.some(s => mensajeNormalizado === s || mensajeNormalizado.startsWith(s + ' '));
  const esPreguntaBot = preguntasBot.some(p => mensajeNormalizado.includes(p));
  
  if ((esSaludo && longitudConsulta < 5) || esPreguntaBot) {
    return 'llama'; // Respuesta rápida con Llama
  }
  
  // ============================================
  // CRITERIO 2: DETECTAR PREGUNTAS QUE BUSCAN EXPLICACIONES PROFUNDAS
  // ============================================
  // Palabras que indican que el usuario quiere una explicación detallada
  const buscaExplicacion = [
    'por que', 'porque', 'xq', 'xque', 'explica', 'explicame', 'como funciona', 'como es que',
    'como se hace', 'que significa', 'que quiere decir', 'no entiendo', 'ayudame a entender',
    'cual es la diferencia', 'diferencia entre', 'compara', 'comparar', 'mejor', 'peor',
    'ventaja', 'desventaja', 'deberia', 'conviene', 'recomiendas', 'recomendacion',
    'que hacer', 'como puedo', 'necesito saber', 'quiero aprender', 'enseñame'
  ];
  
  if (buscaExplicacion.some(exp => mensajeNormalizado.includes(exp))) {
    puntajeComplejidad += 3;
  }
  
  // ============================================
  // CRITERIO 3: CONSULTAS DE SALUD INFANTIL QUE REQUIEREN ANÁLISIS MÉDICO
  // ============================================
  // Términos médicos, síntomas y condiciones de salud
  const terminosMedicos = [
    // Síntomas generales
    'fiebre', 'temperatura', 'calentura', 'enfermo', 'enfermedad', 'dolor', 'duele',
    'tos', 'toser', 'gripe', 'resfriado', 'catarro', 'congestion', 'mocos',
    'vomito', 'vomitar', 'nausea', 'devolver', 'diarrea', 'estreñimiento', 'popo',
    
    // Condiciones y diagnósticos
    'alergia', 'alergico', 'infeccion', 'virus', 'bacteria', 'contagio', 'contagiar',
    'asma', 'bronquitis', 'neumonia', 'otitis', 'conjuntivitis',
    'dermatitis', 'sarpullido', 'ronchas', 'granos', 'piel', 'erupcion',
    
    // Desarrollo físico preocupante
    'no crece', 'bajo peso', 'delgado', 'desnutrido', 'no sube de peso',
    'cabeza grande', 'cabeza chica', 'fontanela', 'mollera',
    
    // Urgencias y señales de alerta
    'sangre', 'sangrado', 'hemorragia', 'convulsion', 'convulsiona', 'tiembla',
    'deshidratacion', 'deshidratado', 'no orina', 'respiracion', 'respira mal',
    'atorado', 'ahoga', 'azul', 'morado', 'inconsciente', 'desmayo'
  ];
  
  const tieneProblemaSalud = terminosMedicos.some(t => mensajeNormalizado.includes(t));
  
  if (tieneProblemaSalud) {
    puntajeComplejidad += 4; // Alto peso para consultas médicas
  }
  
  // ============================================
  // CRITERIO 4: DESARROLLO INFANTIL - PREOCUPACIONES Y RETRASOS
  // ============================================
  // Preocupaciones sobre desarrollo que requieren análisis profundo
  const temasDesarrollo = [
    // Lenguaje y comunicación
    'no habla', 'no dice', 'no comunica', 'mudo', 'tartamudea', 'habla raro',
    'lenguaje', 'palabras', 'vocabulario', 'pronunciacion',
    
    // Motor y movimiento
    'no camina', 'no gatea', 'no se sienta', 'no se para', 'cojea', 'tropieza',
    'torpe', 'coordinacion', 'motricidad', 'motor',
    
    // Alimentación
    'no come', 'come mal', 'rechaza comida', 'quisquilloso', 'picky eater',
    'masticar', 'tragar', 'atraganta', 'alimentacion', 'nutricion',
    
    // Sueño
    'no duerme', 'duerme mal', 'insomnio', 'pesadillas', 'terrores nocturnos',
    'se despierta', 'desvela', 'siesta',
    
    // Comportamiento y emociones
    'llora mucho', 'berrinche', 'rabieta', 'pega', 'muerde', 'agresivo',
    'timido', 'miedoso', 'ansioso', 'triste', 'deprimido',
    'no obedece', 'malcriado', 'rebelde', 'hiperactivo', 'inquieto',
    
    // Desarrollo cognitivo
    'aprender', 'lento', 'atrasado', 'retraso', 'desarrollo',
    'inteligencia', 'capacidad', 'atencion', 'concentracion', 'memoria',
    
    // Hitos y edades
    'meses', 'años', 'edad', 'deberia', 'normal', 'esperable',
    'comparacion', 'otros niños', 'prima', 'hermano'
  ];
  
  if (temasDesarrollo.some(t => mensajeNormalizado.includes(t))) {
    puntajeComplejidad += 3;
  }
  
  // ============================================
  // CRITERIO 5: CONSULTAS EDUCATIVAS PROFUNDAS (PROFESORES)
  // ============================================
  const temasEducativos = [
    // Metodologías y pedagogía
    'metodologia', 'estrategia', 'tecnica', 'actividad', 'ejercicio',
    'planificacion', 'curriculum', 'evaluacion', 'aprendizaje',
    
    // Manejo de aula
    'niño', 'niña', 'alumno', 'estudiante', 'grupo', 'clase', 'aula',
    'enseñar', 'educar', 'instruccion', 'leccion',
    
    // Problemas de conducta
    'conducta', 'comportamiento', 'disciplina', 'normas', 'reglas',
    'atencion', 'concentracion', 'distraccion',
    
    // Habilidades académicas
    'leer', 'lectura', 'escribir', 'escritura', 'contar', 'numeros',
    'letras', 'matematicas', 'ciencias', 'arte'
  ];
  
  if (temasEducativos.some(t => mensajeNormalizado.includes(t))) {
    puntajeComplejidad += 2;
  }
  
  // ============================================
  // CRITERIO 6: CONSULTAS ADMINISTRATIVAS ESPECÍFICAS
  // ============================================
  const temasAdmin = [
    'dinero', 'costo', 'precio', 'pagar', 'cobrar', 'presupuesto', 'gasto', 'ingreso',
    'financiero', 'administrar', 'gestionar', 'personal', 'empleado', 'trabajador',
    'negocio', 'centro', 'guarderia', 'mejorar', 'aumentar', 'reducir', 'ahorrar',
    'marketing', 'promocion', 'clientes', 'padres', 'inscripciones', 'mensualidad'
  ];
  
  if (temasAdmin.some(t => mensajeNormalizado.includes(t))) {
    puntajeComplejidad += 2;
  }
  
  // ============================================
  // CRITERIO 7: MÚLTIPLES PREGUNTAS O SITUACIONES COMPLEJAS
  // ============================================
  const tieneMultiplesPreguntas = (mensaje.match(/\?/g) || []).length > 1;
  if (tieneMultiplesPreguntas) {
    puntajeComplejidad += 2;
  }
  
  // Mensajes largos suelen tener más contexto y necesitar análisis
  if (longitudConsulta > 15) {
    puntajeComplejidad += 2;
  }
  
  // ============================================
  // CRITERIO 8: SITUACIÓN ESPECÍFICA CON DETALLES
  // ============================================
  const situacionEspecifica = ['mi hijo', 'mi hija', 'mi bebe', 'tiene', 'presenta', 'hace', 'desde', 'lleva'];
  if (situacionEspecifica.some(s => mensajeNormalizado.includes(s)) && longitudConsulta > 8) {
    puntajeComplejidad += 2;
  }
  
  // ============================================
  // CRITERIO 9: PREGUNTAS SIMPLES QUE NO NECESITAN ANÁLISIS
  // ============================================
  const preguntasSimples = [
    'si', 'no', 'ok', 'vale', 'gracias', 'adios', 'bye', 'chao',
    'esta bien', 'perfecto', 'entiendo', 'ya', 'ah'
  ];
  
  if (preguntasSimples.some(p => mensajeNormalizado === p) || longitudConsulta < 3) {
    return 'llama';
  }
  
  // Umbral: 3 o más puntos = DeepSeek (necesita razonamiento profundo)
  if (puntajeComplejidad >= 3) {
    return 'deepseek';
  } else {
    return 'llama';
  }
};

/**
 * Función principal para consultar a Ivy Chat
 * @param {string} mensaje - Mensaje del usuario
 * @param {Array} chatHistory - Historial del chat
 * @param {string} userRole - Rol del usuario
 * @returns {Promise<Object>} - Respuesta procesada
 */
export const consultarIvyChat = async (mensaje, chatHistory = [], userRole) => {
  if (!mensaje || !mensaje.trim()) {
    return {
      success: false,
      error: "El mensaje no puede estar vacío"
    };
  }

  // Validar que el rol del usuario sea proporcionado y válido
  const rolesValidos = ['Padre', 'Profesor', 'Administrador'];
  if (!userRole || !rolesValidos.includes(userRole)) {
    return {
      success: false,
      error: "El rol del usuario debe ser proporcionado y ser válido (Padre, Profesor, Administrador)"
    };
  }

  // Analizar permisos del tema: prohibido (p. ej. política) o sensible (p. ej. salud) con restricciones
  const analysis = analyzeTopicPermission(mensaje, userRole);
  if (analysis.prohibited) {
    const normalizado = normalizarTexto(mensaje);
    console.log(`\n❌ BLOQUEADO - Mensaje prohibido para ${userRole}:`);
    console.log(`   Original: "${mensaje}"`);
    console.log(`   Normalizado: "${normalizado}"`);
    console.log(`   prohibited=${analysis.prohibited}, detallesFueraDeRol=${analysis.detallesFueraDeRol}, limited=${analysis.limited}`);

    // Preparar respuesta según el rol con sugerencias específicas (igual que antes)
    let respuestaFueraDeAlcance = "";
    switch (userRole.toLowerCase()) {
      case "profesor":
        respuestaFueraDeAlcance = `Lo siento, como tu asistente especializado en desarrollo infantil y educación, solo puedo ayudarte con temas relacionados con:

🎓 **Temas que SÍ puedo responder:**
• Metodologías de enseñanza para niños (0-6 años)
• Planificación de actividades educativas infantiles
• Evaluación del desarrollo cognitivo y motor
• Estrategias para el manejo del aula en preescolar
• Adaptaciones curriculares para necesidades especiales
• Teorías del aprendizaje aplicadas a la infancia
• Comunicación efectiva con padres de familia
• Detección temprana de dificultades de aprendizaje

¿Te gustaría que te ayude con alguno de estos temas relacionados con tu trabajo como docente?`;
        break;
      case "administrador":
        respuestaFueraDeAlcance = `Lo siento, como tu asistente especializado en gestión de servicios para la infancia, solo puedo ayudarte con temas relacionados con:

💼 **Temas que SÍ puedo responder:**
• Administración de centros educativos infantiles
• Gestión financiera de guarderías y jardines
• Estrategias de marketing para servicios de cuidado infantil
• Administración de recursos humanos en centros educativos
• Indicadores de calidad en educación temprana
• Planificación estratégica para instituciones infantiles
• Cumplimiento de normativas en centros de cuidado
• Optimización de procesos operativos en guarderías

¿Te gustaría que te ayude con algún aspecto de la gestión de tu centro o institución relacionada con la infancia?`;
        break;
      case "padre":
      default:
        respuestaFueraDeAlcance = `Lo siento, soy tu asistente especializado en desarrollo infantil y solo puedo ayudarte con temas relacionados con el cuidado y desarrollo de los niños.

👶 **Temas que SÍ puedo responder:**
• Desarrollo y crecimiento de tu hijo/a
• Estimulación temprana y actividades por edades
• Alimentación infantil y hábitos saludables
• Rutinas de sueño y descanso
• Comportamiento y disciplina positiva
• Hitos del desarrollo (motor, cognitivo, social)
• Preparación para la escuela
• Actividades recreativas y educativas en casa
• Señales de alerta en el desarrollo
• Cuidados básicos de salud infantil

¿Tienes alguna pregunta sobre el desarrollo, cuidado o educación de tu hijo/a?`;
    }

    return {
      success: true,
      data: {
        respuesta: respuestaFueraDeAlcance,
        confianza: {
          nivel: "muy alta",
          puntaje: 1.0,
          fuentes: []
        }
      }
    };
  }

  // Detectar si pide detalles técnicos FUERA de su rol (restricción adicional)
  if (analysis.detallesFueraDeRol) {
    console.log(`Detalles técnicos fuera de rol detectados para ${userRole}: "${mensaje}"`);
    
    let respuestaDetallesFueraDeRol = "";
    switch (userRole.toLowerCase()) {
      case "profesor":
        respuestaDetallesFueraDeRol = `Lo entiendo, pero como especialista en educación y desarrollo infantil, no puedo profundizar en detalles administrativos o técnicos muy específicos que están fuera de mi área de expertise.

📚 **Mi especialización es:** Pedagogía, metodologías educativas, desarrollo infantil, didáctica y diseño de actividades.

Para detalles muy técnicos en administración o medicina especializada, te recomendaría consultar con:
• 💼 Un especialista en administración/gestión educativa (para temas administrativos complejos)
• 🏥 Un profesional médico o especialista (para diagnósticos y procedimientos médicos)

¿Hay algo sobre educación, pedagogía o desarrollo infantil en lo que pueda ayudarte?`;
        break;
      case "administrador":
        respuestaDetallesFueraDeRol = `Lo entiendo, pero como especialista en administración y gestión de centros, no puedo profundizar en detalles pedagógicos o técnicos muy específicos que están fuera de mi área de expertise.

💼 **Mi especialización es:** Administración de centros, gestión de recursos, finanzas educativas, estrategia institucional y cumplimiento normativo.

Para detalles muy técnicos en pedagogía o medicina especializada, te recomendaría consultar con:
• 🎓 Un especialista en pedagogía (para diseño curricular y metodologías educativas)
• 🏥 Un profesional médico o especialista (para diagnósticos y procedimientos médicos)

¿Hay algo sobre administración, gestión o estrategia institucional en lo que pueda ayudarte?`;
        break;
      case "padre":
      default:
        respuestaDetallesFueraDeRol = `Lo entiendo, pero como especialista en desarrollo y cuidado infantil, no puedo profundizar en detalles muy técnicos que están fuera de mi área de expertise.

👶 **Mi especialización es:** Desarrollo infantil, cuidado diario, crianza, estimulación temprana y educación básica del niño.

Para detalles muy técnicos en pedagogía especializada, administración o medicina, te recomendaría consultar con:
• 🎓 Un especialista en pedagogía (para metodologías educativas avanzadas)
• 💼 Un administrador educativo (para decisiones administrativas)
• 🏥 Un profesional médico (para diagnósticos y tratamientos especializados)

¿Hay algo sobre el desarrollo o cuidado básico de tu hijo/a en lo que pueda ayudarte?`;
    }
    
    return {
      success: true,
      data: {
        respuesta: respuestaDetallesFueraDeRol,
        confianza: {
          nivel: "alta",
          puntaje: 0.85,
          fuentes: []
        }
      }
    };
  }

  // Si el tema es sensible (ej. salud) no bloqueamos la conversación pero marcaremos que la respuesta debe ser limitada
  const isLimited = analysis.limited;

  // Verificar si es un saludo simple
  const saludos = ["hola", "buenos días", "buenas tardes", "buenas noches", "hi", "hello", "ola", "hla", "hey"];
  const mensajeNormalizado = normalizarTexto(mensaje);
  const esSaludoSimple = saludos.some(s => mensajeNormalizado.includes(s)) && mensaje.trim().split(" ").length < 4;
  
  if (esSaludoSimple) {
    // Personalizar el saludo según el rol
    let saludoPersonalizado = "";
    
    switch (userRole.toLowerCase()) {
      case "profesor":
        saludoPersonalizado = "¡Hola! Soy Ivy, tu asistente especializado en desarrollo infantil y educación. Puedo ayudarte con consultas técnicas sobre pedagogía, metodologías educativas y desarrollo infantil. ¿En qué puedo ayudarte hoy?";
        break;
      case "administrador":
        saludoPersonalizado = "¡Hola! Soy Ivy, tu asistente especializado en gestión y administración de servicios relacionados con la infancia. Puedo ayudarte con temas de administración, finanzas y gestión de recursos en organizaciones educativas y de cuidado infantil. ¿En qué puedo asistirte hoy?";
        break;
      case "padre":
      default:
        saludoPersonalizado = "¡Hola! Soy Ivy, tu asistente especializado en desarrollo infantil. ¿En qué puedo ayudarte hoy con respecto al cuidado o desarrollo de tu hijo/a?";
    }
    
    return {
      success: true,
      data: {
        respuesta: saludoPersonalizado,
        confianza: {
          nivel: "alta",
          puntaje: 0.95,
          fuentes: []
        }
      }
    };
  }

  // SISTEMA INTELIGENTE DE ENRUTAMIENTO DE CONSULTAS
  // Clasifica la consulta para determinar qué modelo usar
  const modeloSeleccionado = clasificarComplejidadConsulta(mensaje, userRole);
  
  if (modeloSeleccionado === 'deepseek') {
    // Usar DeepSeek para consultas que requieren alto razonamiento
    // Sin timeout - permitir todo el tiempo necesario
    try {
      console.log('🧠 Consultando DeepSeek R1 Turbo (consulta compleja)...');
      
      const resultado = await consultarDeepSeek(mensaje, chatHistory, userRole);
      
      if (resultado.success) {
          console.log('✅ Respuesta exitosa de DeepSeek');
          let textoRespuesta = typeof resultado.data === 'string' ? resultado.data : resultado.data.respuesta;
          if (isLimited) {
            textoRespuesta += '\n\n⚠️ Nota: Puedo ofrecer orientación general sobre este tema, pero no puedo proporcionar diagnósticos ni recomendaciones médicas detalladas. Para decisiones clínicas o medicamentos específicos, consulta con un profesional de salud (pediatra).';
          }
          return {
            success: true,
            data: {
              respuesta: textoRespuesta,
              modelo: 'DeepSeek R1 Turbo',
              confianza: {
                nivel: "muy alta",
                puntaje: 0.95,
                fuentes: []
              }
            }
          };
        }
    } catch (error) {
      console.log('⚠️ DeepSeek falló, usando Llama como fallback:', error.message);
    }
  }

  // Usar Llama directamente para consultas simples/cotidianas
  // O como fallback si DeepSeek falla
  console.log('💬 Consultando Llama 3.1 8B Instruct (consulta cotidiana o fallback)...');
  const respuesta = await consultarLlama(mensaje, chatHistory, userRole);

  // Si Llama falla, devolver mensaje de error
  if (!respuesta.success) {
    return {
      success: false,
      error: "Lo siento, no puedo procesar tu consulta en este momento. Por favor, intenta nuevamente más tarde."
    };
  }

  // Devolver la respuesta formateada (Llama)
  console.log('✅ Respuesta exitosa de Llama');
  let textoRespuestaFinal = typeof respuesta.data === 'string' ? respuesta.data : respuesta.data.respuesta;
  if (isLimited) {
    textoRespuestaFinal += '\n\n⚠️ Nota: Puedo ofrecer orientación general sobre este tema, pero no puedo proporcionar diagnósticos ni recomendaciones médicas detalladas. Para decisiones clínicas o medicamentos específicos, consulta con un profesional de salud (pediatra).';
  }
  return {
    success: true,
    data: {
      respuesta: textoRespuestaFinal,
      modelo: 'Llama 3.1 8B Instruct',
      confianza: {
        nivel: modeloSeleccionado === 'llama' ? "alta" : "media",
        puntaje: modeloSeleccionado === 'llama' ? 0.85 : 0.75,
        fuentes: []
      }
    }
  };
};

/**
 * Función para obtener el prompt base universal
 * @returns {string} - Prompt base común para todos los roles
 */
const obtenerPromptUniversal = () => {
  return `Sistema: Eres Ivy, un asistente especializado en desarrollo infantil. 
Ignora los errores ortográficos o gramaticales del usuario y responde a la intención de sus preguntas.`;
};

/**
 * Función para obtener el prompt específico por rol
 * @param {string} rol - Rol del usuario
 * @returns {string} - Prompt específico del rol
 */
const obtenerPromptPorRol = (rol) => {
  switch (rol.toLowerCase()) {
    case "profesor":
      return `
INSTRUCCIONES PARA ROL DE PROFESOR:
- Usa terminología técnica y académica del ámbito educativo
- Proporciona información basada en teorías pedagógicas y evidencia científica
- Responde consultas sobre metodologías educativas, evaluación e intervención
- Discute enfoques pedagógicos y curriculares con profundidad teórica
- Puedes abordar temas como necesidades educativas especiales, adaptaciones curriculares
- Da respuestas fundamentadas en la literatura científica cuando sea posible
- Mantén un tono profesional pero accesible`;
      
    case "administrador":
      return `
INSTRUCCIONES PARA ROL DE ADMINISTRADOR:
- Abordar consultas sobre gestión de instituciones educativas y de cuidado infantil
- Responder sobre aspectos financieros, legales y administrativos
- Proporcionar información sobre marketing, recursos humanos y operaciones
- Dar orientación sobre análisis de mercado y planificación estratégica
- Discutir indicadores de desempeño y optimización de procesos
- Mantener un enfoque profesional orientado a resultados
- Usar terminología empresarial apropiada`;
      
    case "padre":
    default:
      return `
INSTRUCCIONES PARA ROL DE PADRE:
- Usar lenguaje sencillo y accesible, evitando tecnicismos innecesarios
- Proporcionar consejos prácticos sobre crianza y cuidado diario
- Ofrecer recomendaciones concretas para estimulación y desarrollo
- Mantener un tono cálido, empático y comprensivo
- Dar pautas claras sobre hitos del desarrollo y señales de alerta
- Sugerir actividades y recursos apropiados para cada etapa
- Recordar consultar a profesionales cuando corresponda`;
  }
};

/**
 * Función para procesar el historial de conversación
 * @param {Array} historial - Historial del chat
 * @returns {string} - Historial formateado
 */
const procesarHistorial = (historial) => {
  return historial.length > 0 
    ? historial.slice(-3).map(msg => 
        `${msg.type === 'user' ? 'Usuario' : 'Asistente'}: ${msg.content}`).join('\n') 
    : 'No hay historial previo';
};

/**
 * Función para generar instrucciones contextuales específicas
 * @param {string} rol - Rol del usuario
 * @param {string} mensaje - Mensaje del usuario
 * @returns {string} - Instrucciones contextuales
 */
const generarInstruccionesContextuales = (rol, mensaje) => {
  return `
Instrucciones para responder:
1. MANTENER CONTEXTO:
   - Usa el historial de la conversación para dar respuestas coherentes
   - Haz referencias a mensajes anteriores cuando sea relevante
   - Si el usuario hace preguntas de seguimiento, mantén el contexto
   - Pide aclaraciones si es necesario para mantener la coherencia

2. CAPACIDADES (SEGÚN ROL ${rol.toUpperCase()}):
   - Responder preguntas sobre desarrollo infantil y estimulación temprana
   ${rol.toLowerCase() === "profesor" ? 
     "- Abordar temas pedagógicos, metodológicos y técnicos de educación infantil\n   - Discutir teorías educativas y enfoques didácticos\n   - Proporcionar información basada en evidencia científica" : 
     rol.toLowerCase() === "administrador" ? 
     "- Responder sobre gestión, finanzas y administración de centros infantiles\n   - Abordar temas de marketing, recursos humanos y operaciones\n   - Dar orientación sobre estrategias y optimización de procesos" : 
     "- Proporcionar información sobre hitos del desarrollo\n   - Sugerir actividades de estimulación según la edad\n   - Dar recomendaciones básicas sobre salud infantil"}
   - Orientar sobre problemas comunes en el desarrollo

3. ESTILO DE RESPUESTA:
   - Mantén un tono ${rol.toLowerCase() === "profesor" ? "profesional y académico" : rol.toLowerCase() === "administrador" ? "profesional y estratégico" : "empático y cercano"}
   - Sé conciso pero informativo
   - Usa lenguaje ${rol.toLowerCase() === "profesor" || rol.toLowerCase() === "administrador" ? "técnico apropiado pero comprensible" : "claro y accesible"}
   - Haz preguntas de seguimiento cuando sea apropiado
   - Mantén una conversación natural y fluida

4. SALUD Y SEGURIDAD:
   - Para temas de salud básica, da recomendaciones generales
   - En casos graves, recomienda consultar al pediatra
   - Menciona signos de alarma cuando sea relevante

5. ERRORES ORTOGRÁFICOS:
   - Interpreta mensajes con errores de escritura
   - Entiende variaciones en la escritura de palabras clave
   - No corrijas explícitamente los errores del usuario

6. RESPUESTA:
   - Responde en español
   - Adapta tu respuesta al rol ${rol.toUpperCase()} del usuario
   - Proporciona información relevante y útil según el contexto
   - Mantén coherencia con respuestas anteriores`;
};

/**
 * Obtiene un prompt completo para la consulta al modelo
 * @param {string} mensajeUsuario - Mensaje del usuario
 * @param {Array} chatHistory - Historial del chat
 * @param {string} userRole - Rol del usuario
 * @returns {string} - Prompt completo
 */
export const obtenerPromptCompleto = (mensajeUsuario, chatHistory = [], userRole) => {
  // Validar que el rol del usuario sea proporcionado y válido
  const rolesValidos = ['Padre', 'Profesor', 'Administrador'];
  if (!userRole || !rolesValidos.includes(userRole)) {
    throw new Error('El rol del usuario debe ser proporcionado y ser válido (Padre, Profesor, Administrador)');
  }

  const prompt_base = obtenerPromptUniversal();
  const prompt_rol = obtenerPromptPorRol(userRole);
  const contexto_conversacion = procesarHistorial(chatHistory);
  const instrucciones_especificas = generarInstruccionesContextuales(userRole, mensajeUsuario);
  
  // NOTA: Las funciones obtenerPreguntasBasicas, obtenerPreguntasPorRol y validarTemasPorRol 
  // se mantienen en el código como referencia pero ya no se utilizan en la nueva lógica permisiva.
  // Invocar para evitar warning de no utilizadas (se usa console.log aunque sea para debug)
  if (console.log && obtenerPreguntasBasicas && obtenerPreguntasPorRol && validarTemasPorRol) {
    // Llamada dummy para evitar lint warnings
  }
  
  return `${prompt_base}
${prompt_rol}

Historial de la conversación:
${contexto_conversacion}

Pregunta actual del usuario: ${mensajeUsuario}
${instrucciones_especificas}`;
};

// Ejemplo de uso:
/*
const main = async () => {
  // Prueba para padre
  console.log("=== PRUEBA PARA PADRE ===");
  const respuestaPadre = await consultarIvyChat("Mi bebé de 10 mese tine fievre, ke puedo aser?", [], "Padre");
  console.log(respuestaPadre);
  
  // Prueba para profesor
  console.log("\n=== PRUEBA PARA PROFESOR ===");
  const respuestaProfesor = await consultarIvyChat("Qué métodos de evaluacion cognitiva recomiendas para niños de 4 años?", [], "Profesor");
  console.log(respuestaProfesor);
  
  // Prueba para administrador
  console.log("\n=== PRUEBA PARA ADMINISTRADOR ===");
  const respuestaAdmin = await consultarIvyChat("Cómo optimizar los costos operativos de una guardería?", [], "Administrador");
  console.log(respuestaAdmin);
};

main();
*/
